// pages/LessonPlan.tsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  MenuItem,
  Select,
  Typography,
  Paper,
  CircularProgress,
} from "@mui/material";
import CommonTable2, {
  type Column,
} from "../../Components/CommonTable/CommonTable2";
import { useAppDispatch, useAppSelector } from "../../../../Redux/Store/Hooks";
import { LessonPlanRead } from "../../../../Redux/Slices/Booklet/Tutor/LessonPlan/LessonPlanRead";

// Table row type after flattening API response
interface LessonDriveRow {
  yearGroup: string;
  subject: string;
  topic: string;
  fileName: string;
  fileLink: string;
}

// 🔄 transform nested API response → flat table rows
const transformResponseToRows = (data: any[]): LessonDriveRow[] => {
  const rows: LessonDriveRow[] = [];

  data.forEach((item) => {
    const yearGroup = item.year_group;
    const driveData = item.drive_data;

    if (!driveData.children?.length) {
      rows.push({
        yearGroup,
        subject: "-",
        topic: "-",
        fileName: driveData.name,
        fileLink: driveData.webViewLink,
      });
    }

    driveData.children?.forEach((subjectFolder: any) => {
      const subject = subjectFolder.name;

      subjectFolder.children?.forEach((topicFolder: any) => {
        const topic = topicFolder.name;

        topicFolder.children?.forEach((file: any) => {
          rows.push({
            yearGroup,
            subject,
            topic,
            fileName: file.name,
            fileLink: file.webViewLink,
          });
        });
      });
    });
  });

  return rows;
};

const LessonPlan: React.FC = () => {
  const dispatch = useAppDispatch();
  const { data, isLoading } = useAppSelector(
    (state) => state.LessonPlanReadSlice
  );

  const [yearGroup, setYearGroup] = useState("");
  const [subject, setSubject] = useState("");
  const [topic, setTopic] = useState("");

  const allRows: LessonDriveRow[] = useMemo(
    () => (data?.data ? transformResponseToRows(data.data) : []),
    [data]
  );

  // 📌 Filtered rows
  const [filteredRows, setFilteredRows] = useState<LessonDriveRow[]>([]);

  useEffect(() => {
    dispatch(LessonPlanRead());
  }, [dispatch]);

  useEffect(() => {
    setFilteredRows(allRows);
  }, [allRows]);

  const handleSearch = () => {
    const filtered = allRows.filter((row) => {
      const matchYear = yearGroup ? row.yearGroup === yearGroup : true;
      const matchSubject = subject ? row.subject === subject : true;
      const matchTopic = topic ? row.topic === topic : true;
      return matchYear && matchSubject && matchTopic;
    });

    setFilteredRows(filtered);
  };

  // 📌 Build dynamic filter options
  const yearGroups = Array.from(new Set(allRows.map((r) => r.yearGroup)));
  const subjects = Array.from(new Set(allRows.map((r) => r.subject)));
  const topics = Array.from(new Set(allRows.map((r) => r.topic)));

  const columns: Column[] = [
    { field: "yearGroup", headerName: "Year Group" },
    { field: "subject", headerName: "Subject" },
    { field: "topic", headerName: "Topic" },
    { field: "fileName", headerName: "File Name" },
  ];

  return (
    <Box p={3}>
      <Typography variant="h6" gutterBottom>
        Lesson Plan
      </Typography>

      {/* Filters */}
      <Box display="flex" gap={2} mb={2}>
        {/* Year Group */}
        <Select
          value={yearGroup}
          onChange={(e) => setYearGroup(e.target.value)}
          displayEmpty
          fullWidth
        >
          <MenuItem value="">Year Group</MenuItem>
          {yearGroups.map((yg) => (
            <MenuItem key={yg} value={yg}>
              {yg}
            </MenuItem>
          ))}
        </Select>

        {/* Subject */}
        <Select
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          displayEmpty
          fullWidth
        >
          <MenuItem value="">Subject</MenuItem>
          {subjects.map((subj) => (
            <MenuItem key={subj} value={subj}>
              {subj}
            </MenuItem>
          ))}
        </Select>

        {/* Topic */}
        <Select
          value={topic}
          onChange={(e) => setTopic(e.target.value)}
          displayEmpty
          fullWidth
        >
          <MenuItem value="">Topic</MenuItem>
          {topics.map((t) => (
            <MenuItem key={t} value={t}>
              {t}
            </MenuItem>
          ))}
        </Select>

        <Button
          variant="contained"
          sx={{
            fontSize: "14px",
            color: "#ffffff",
            bgcolor: "#009DFF",
            borderRadius: "20px",
            textTransform: "none",
            "&:hover": {
              opacity: 0.8,
              bgcolor: "#009DFF",
            },
          }}
          onClick={handleSearch}
        >
          Search
        </Button>
      </Box>

      {/* Table */}
      <Paper sx={{ p: 2 }}>
        {isLoading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="200px"
          >
            <CircularProgress />
          </Box>
        ) : (
          <CommonTable2
            columns={columns}
            rows={filteredRows.map((row) => ({ ...row }))}
          />
        )}

        {/* <Box mt={2} textAlign="left">
          <Button
            variant="text"
            color="primary"
            onClick={() => setFilteredRows(allRows)}
          >
            View More
          </Button>
        </Box> */}
      </Paper>
    </Box>
  );
};

export default LessonPlan;
